import { useId } from "react";
import { Select, SelectItem } from "@/components/ui/select";

/**
 * The admission cohorts the Statistics page can filter on. There is no
 * `admission_year` column (chốt ở plan.md Non-goals) — the value here is sent
 * to the backend as-is and only turned into a `MaSV` prefix ("26"/"25")
 * server-side, in `base-statistics.service.ts`'s `admissionYearPrefix`. Fixed
 * to two entries by design (step 2 of the phase plan): "no fetch", so this is
 * a component-local constant, not a catalog call like `FacultyFilter`'s khoa
 * list.
 */
export const ADMISSION_YEARS = ["2026", "2025"] as const;

export type AdmissionYear = (typeof ADMISSION_YEARS)[number];

/**
 * Default cohort for the Statistics page. Exported so the page that owns
 * `filters` state (`StatisticsPage.tsx`) can initialise it with the same
 * constant instead of duplicating the literal "2026" — see the phase 4
 * report for the exact wiring the orchestrator (phase 6) is expected to do.
 */
export const DEFAULT_ADMISSION_YEAR: AdmissionYear = "2026";

interface CohortFilterProps {
	value?: string;
	onChange: (admissionYear: string | undefined) => void;
	className?: string;
}

/**
 * Chọn khoá tuyển sinh (2026 / 2025) để thu hẹp phạm vi thống kê.
 *
 * Đứng cạnh `FacultyFilter` ở đầu trang, cùng lý do: lựa chọn là của cả trang,
 * dựng lại control trong từng tab sẽ hiện cùng một giá trị ở nhiều chỗ.
 *
 * Khoá suy từ tiền tố `MaSV`, không phải một cột lọc thật trên `student` —
 * server đọc nó qua `filter.admissionYear` và tự map sang `startsWith`. Một
 * giá trị lạ hoặc rỗng ở server nghĩa là "không lọc", không phải lỗi, nên
 * control này không cần validate gì thêm ngoài việc chỉ đưa ra hai lựa chọn
 * hợp lệ.
 */
export function CohortFilter({ value, onChange, className }: CohortFilterProps) {
	const id = useId();

	return (
		<div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
			<Select
				id={id}
				// Nhãn ở `aria-label`, không phải thẻ <label> phía trên — cùng lý do
				// với `FacultyFilter`: control này sống trong hàng nút của header, một
				// nhãn xếp trên sẽ đội hàng đó cao lên (CLAUDE.md quy tắc 4, 5).
				aria-label="Khoá"
				value={value ?? DEFAULT_ADMISSION_YEAR}
				onChange={(e) => onChange(e.target.value || undefined)}
				// h-8 để bằng FacultyFilter và các nút `size="sm"` cùng hàng. Không
				// trộn chiều cao trong cùng một hàng (CLAUDE.md quy tắc 5).
				className="h-8 w-28 text-xs"
			>
				{ADMISSION_YEARS.map((year) => (
					<SelectItem key={year} value={year}>
						Khoá {year}
					</SelectItem>
				))}
			</Select>
		</div>
	);
}
